import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Play, X, ChevronLeft, ChevronRight, Film, Image as ImageIcon, Loader2, Star } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Galerie 100% DYNAMIQUE — alimentée par l'administration via « Alimentation du site ».
// Publication instantanée : tout contenu actif (type photo/vidéo) apparaît ici immédiatement.
export default function Gallery() {
  const [tab, setTab] = useState("photos");
  const [lightbox, setLightbox] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/site-content`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Les contenus « À la une » (mis en avant) s'affichent en premier et en GRAND
  const byFeatured = (a, b) => (b.is_featured === true) - (a.is_featured === true);
  const photos = items.filter(i => i.type === "gallery" && i.media_url).sort(byFeatured);
  const videos = items.filter(i => i.type === "video" && i.media_url).sort(byFeatured);

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox(l => (l - 1 + photos.length) % photos.length);
  const next = () => setLightbox(l => (l + 1) % photos.length);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="page-gallery">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1E3A8A] to-[#166534] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-dot-pattern" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-5 inline-flex"><Camera className="h-3 w-3" /><span>Galerie photos & vidéos</span></div>
          <h1 className="mazid-headline text-5xl sm:text-6xl lg:text-7xl mb-5 max-w-4xl tracking-[-0.03em]">
            La vie aux <span className="text-[#FBBF24]">Écoles Mazid</span>, en images.
          </h1>
          <p className="text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed">
            Découvrez nos infrastructures et nos moments forts. Cette galerie est mise à jour en continu par l&apos;école.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-20 z-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center gap-2 py-4 overflow-x-auto">
          <button
            onClick={() => setTab("photos")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${tab === "photos" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="gallery-tab-photos"
          >
            <ImageIcon className="h-4 w-4" /> Photos ({photos.length})
          </button>
          <button
            onClick={() => setTab("videos")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${tab === "videos" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="gallery-tab-videos"
          >
            <Film className="h-4 w-4" /> Vidéos ({videos.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-7 w-7 animate-spin mx-auto text-slate-400" /></div>
          ) : tab === "photos" ? (
            photos.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center text-slate-500" data-testid="gallery-empty-photos">
                Aucune photo publiée pour le moment.
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {photos.map((p, i) => (
                  <motion.button
                    key={p.id}
                    onClick={() => openLightbox(i)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className={`group relative aspect-square rounded-2xl overflow-hidden bg-slate-200 ${p.is_featured ? "sm:col-span-2 sm:row-span-2 ring-4 ring-[#FBBF24]" : ""}`}
                    data-testid={`gallery-photo-${i}`}
                  >
                    <img src={p.media_url} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    {p.is_featured && (
                      <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-[#FBBF24] text-[#0F172A] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-lg">
                        <Star className="h-2.5 w-2.5 fill-current" /> À la une
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-left text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-sm font-semibold">{p.title}</div>
                      {p.description && <div className="text-xs text-slate-300 mt-0.5 line-clamp-2">{p.description}</div>}
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            )
          ) : videos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center text-slate-500" data-testid="gallery-empty-videos">
              Aucune vidéo publiée pour le moment.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map((v, i) => (
                <div key={v.id} className="rounded-2xl overflow-hidden bg-black shadow-xl" data-testid={`gallery-video-${i}`}>
                  <div className="aspect-video relative">
                    <video src={v.media_url} controls className="w-full h-full object-cover">
                      Votre navigateur ne supporte pas la vidéo.
                    </video>
                  </div>
                  <div className="p-5 bg-white">
                    <div className="font-heading font-bold text-[#1E3A8A]">{v.title}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Play className="h-3 w-3" /> {v.description || "Vidéo"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && photos[lightbox] && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button onClick={closeLightbox} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-close">
            <X className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-next">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={photos[lightbox].media_url} alt={photos[lightbox].title} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <div className="text-center text-white mt-5">
              <div className="font-heading font-bold text-xl">{photos[lightbox].title}</div>
              {photos[lightbox].description && <div className="text-sm text-slate-300 mt-1">{photos[lightbox].description}</div>}
              <div className="text-xs text-slate-400 mt-2">{lightbox + 1} / {photos.length}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
